import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { PvpStepNav } from "../components/PvpStepNav";
import { PvpSectionForm } from "../components/PvpSectionForm";
import { PvpSummaryStep } from "../components/PvpSummaryStep";
import { ArrowLeft, ArrowRight, SkipForward, Check } from "lucide-react";

export default function PvpPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["superadmin", "consultant", "org_admin"].includes(user.role);
  const [schema, setSchema] = useState([]);
  const [pvp, setPvp] = useState(null);
  const [draft, setDraft] = useState({});
  const [step, setStep] = useState(0);
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    let live = true;
    api.get("/pvp/schema").then(({ data }) => live && setSchema(data));
    api.get("/organizations").then(({ data }) => live && setOrgs(data));
    api.get(`/pvps/${id}`).then(({ data }) => {
      if (!live) return;
      setPvp(data);
      setDraft(data.sections);
      setStep(data.current_step || 0);
    }, () => live && toast.error("PVP niet gevonden"));
    return () => {
      live = false;
    };
  }, [id]);

  if (!pvp || !schema.length) return <div className="p-10 font-mono text-sm text-zinc-500">PVP laden…</div>;

  const steps = [...schema, { key: "summary", title: "Management samenvatting" }];
  const current = steps[Math.min(step, steps.length - 1)];
  const persist = async (status, nextStep) => {
    if (!canEdit) return setStep(nextStep);
    try {
      const body = { current_step: nextStep };
      if (current.key !== "summary") {
        body.sections = { [current.key]: draft[current.key] };
        if (status) body.section_status = { [current.key]: status };
      }
      const { data } = await api.patch(`/pvps/${id}`, body);
      setPvp(data);
      setStep(nextStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Opslaan mislukt");
    }
  };
  const last = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-white" data-testid="pvp-page">
      <Header orgs={orgs} currentOrgId={pvp.organization_id} onSelectOrg={(o) => navigate(`/employees?org=${o}`)} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <button onClick={() => navigate(`/employees?org=${pvp.organization_id}`)} className="font-mono text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1 mb-4" data-testid="pvp-back-button">
          <ArrowLeft className="h-3 w-3" /> Medewerkers
        </button>
        <div className="border-b border-zinc-900 pb-6 mb-8">
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">Prestatie Verbeter Plan · {pvp.employee_name} · {pvp.strategy_name}</div>
          <h1 className="font-grotesk text-3xl sm:text-4xl font-bold tracking-tight max-w-3xl" data-testid="pvp-behavior">{pvp.behavior_text}</h1>
          {pvp.ai_prefilled && <p className="text-xs text-zinc-500 mt-2">Concept vooraf ingevuld door AI op basis van wat al bekend is. Controleer en vul aan; [tekst tussen haken] vraagt om jouw input.</p>}
        </div>
        <div className="grid md:grid-cols-[240px_minmax(0,1fr)] gap-10">
          <PvpStepNav steps={steps} current={step} status={pvp.section_status || {}} summaryDone={!!pvp.management_summary} onSelect={(i) => persist(null, i)} />
          <section>
            <div className="font-mono text-xs text-zinc-400 mb-1">Stap {step + 1} van {steps.length}</div>
            <h2 className="font-grotesk text-2xl font-bold mb-6" data-testid="pvp-step-title">{current.title}</h2>
            {current.key === "summary" ? (
              <PvpSummaryStep pvp={pvp} canEdit={canEdit} onChange={setPvp} />
            ) : (
              <PvpSectionForm section={current} values={draft[current.key] || {}} all={draft} pvp={pvp} onPvp={setPvp} disabled={!canEdit} onChange={(vals) => setDraft({ ...draft, [current.key]: vals })} />
            )}
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-zinc-200">
              <Button variant="outline" className="rounded-none" disabled={step === 0} onClick={() => persist(null, step - 1)} data-testid="pvp-prev-button">
                <ArrowLeft className="h-4 w-4 mr-1" /> Vorige
              </Button>
              <div className="flex-1" />
              {!last && canEdit && (
                <Button variant="outline" className="rounded-none" onClick={() => persist("skipped", step + 1)} data-testid="pvp-skip-button">
                  <SkipForward className="h-4 w-4 mr-1" /> Overslaan, later invullen
                </Button>
              )}
              {!last ? (
                <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={() => persist("done", step + 1)} data-testid="pvp-next-button">
                  {canEdit ? "Opslaan & verder" : "Volgende"} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={() => navigate(`/employees?org=${pvp.organization_id}`)} data-testid="pvp-finish-button">
                  <Check className="h-4 w-4 mr-1" /> Klaar
                </Button>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
